import { trackPromptMarks } from './terminalPromptMarks';
import { loadWebLinks } from './terminalWebLinks';

/**
 * The xterm add-ons every terminal (local and SSH) loads after `term.open`.
 *
 * Unicode 11 widths (Terminal board card 1, 21.10.2026): xterm's built-in
 * table is Unicode 6, where most emoji and the Nerd-Font/Powerline glyphs of
 * Oh My Zsh / Starship prompts count as ONE cell while the shell counts two.
 * The cursor then drifts one column per glyph and typed text lands on top of
 * the prompt — another source of the "random characters" on Macs, where those
 * prompts are the norm.
 *
 * Scrollback search (card 2): `@xterm/addon-search`, driven by the pane's
 * search bar (components/terminal/TerminalSearchBar.js).
 *
 * Clickable URLs (card 4): `@xterm/addon-web-links` via terminalWebLinks.js.
 *
 * Returns the loaded add-ons; a failed import leaves the terminal working.
 */
export async function loadTerminalAddons(term) {
  // Not an xterm add-on, but per terminal like one: prompt lines for
  // "Copy last output" (card 3).
  const loaded = { prompts: trackPromptMarks(term) };
  try {
    const { Unicode11Addon } = await import('@xterm/addon-unicode11');
    const unicode = new Unicode11Addon();
    term.loadAddon(unicode);
    term.unicode.activeVersion = '11';
    loaded.unicode = unicode;
  } catch (err) {
    console.warn('[terminal] unicode11 add-on not loaded', err);
  }
  try {
    const { SearchAddon } = await import('@xterm/addon-search');
    const search = new SearchAddon({ highlightLimit: 1000 });
    term.loadAddon(search);
    loaded.search = search;
  } catch (err) {
    console.warn('[terminal] search add-on not loaded', err);
  }
  try {
    const { WebLinksAddon } = await import('@xterm/addon-web-links');
    loaded.links = loadWebLinks(term, WebLinksAddon);
  } catch (err) {
    console.warn('[terminal] web-links add-on not loaded', err);
  }
  return loaded;
}
